import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { GlgTooltip } from '../../shared/components'
import useScrollState from '../hooks/useScrollState'
import { formatSummaryValue } from '../utils/summary_utils'
import { pickTranslatedName } from '../utils/events_utils'
import { VIEW_MODES } from '../constants'
import { componentText } from '../utils/i18n_utils'

const t = (key, fallback) => componentText(`summaries.${key}`, fallback)

const formatTooltipDate = (isoDate) => {
  if (!isoDate) { return '' }
  const locale = window.I18n ? (window.I18n.locale === 'jp' ? 'ja' : window.I18n.locale) : undefined
  const date = new Date(`${isoDate}T00:00:00`)
  return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
}

// The events/trips summary table (league-wide payload): `metric` picks the
// value formatting and labels, `isTeam` the entity column label, `expanded`
// the per-tournament columns. Web and landscape keep round/total sticky right
// with the tournament columns between name and round; portrait (mobile) keeps
// only name sticky and appends the tournament columns after total, in play
// order (T1..Tn). A League's round-scoped payload renders LeagueSummaryTable
// instead (RTM-10251).
const EventSummaryTable = ({
  metric,
  isTeam,
  expanded,
  tournaments = [],
  rows = [],
  currencySymbol,
  purseDecimalPlaces,
  viewMode,
}) => {
  const scrollRef = useRef(null)
  const { isAtStart, isAtEnd, isScrollable, checkScrollable } = useScrollState(scrollRef)

  // Collapsing blocks the overflow without clearing the offset, so rewind the
  // scroll explicitly, then re-measure (same pattern as ResultScope).
  useEffect(() => {
    if (!expanded && scrollRef.current) { scrollRef.current.scrollLeft = 0 }
    checkScrollable()
  }, [ expanded, checkScrollable ])

  const displayColumns = tournaments.map((tournament, index) => ({
    tournament,
    label: `T${index + 1}`,
  }))

  const isMobileView = [ VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE ].includes(viewMode)

  const formatValue = (value) => formatSummaryValue(metric, value, currencySymbol, purseDecimalPlaces)

  const entityLabel = isTeam ? t('team', 'Team') : t('player', 'Player')
  const roundLabel = metric === 'purse' ? t('round_purse', 'Round Purse') : t('round_points', 'Round Points')
  // Collapsed keeps the columns mounted with the scroll blocked (mirroring
  // ResultScope), so the fades must also be gated on the expanded state.
  const canScroll = expanded && isScrollable
  const showStartGradient = canScroll && !isAtEnd

  const tournamentHeaderCells = displayColumns.map(({ tournament, label }) => {
    const tournamentName = pickTranslatedName(tournament.name_translations, tournament.name)
    return (
      <th scope="col" key={tournament.id} className="lbv2__summary-t-col">
        <div className="lbv2__summary-t-header">
          <span>{label}</span>
          <GlgTooltip
            tooltip={
              <div className="lbv2__summary-tooltip">
                {tournament.date && <div>{formatTooltipDate(tournament.date)}</div>}
                <div>{tournamentName}</div>
              </div>
            }
            placement="bottom"
          >
            <button
              type="button"
              className="lbv2__summary-info-btn"
              aria-label={`${tournamentName}${tournament.date ? `, ${formatTooltipDate(tournament.date)}` : ''}`}
            >
              <i className="gga-info-circle" />
            </button>
          </GlgTooltip>
        </div>
      </th>
    )
  })

  const tournamentValueCells = (row) => displayColumns.map(({ tournament }) => (
    <td key={tournament.id} className="lbv2__summary-t-col">
      {formatValue(row.valuesByTournament[tournament.id])}
    </td>
  ))

  return (
    <div className={`lbv2__summary-scope ${isMobileView && showStartGradient ? 'at-start' : ''}`}>
      <div
        className={`lbv2__scroll-wrapper ${expanded ? 'expanded' : ''} ${isScrollable ? 'scrollable' : ''} ${canScroll && !isAtStart ? 'scrolled' : ''} ${showStartGradient ? 'at-start' : ''}`}
        ref={scrollRef}
      >
        <table className="lbv2__summary-table">
          <thead>
            <tr>
              <th scope="col" className="lbv2__summary-name">{entityLabel}</th>
              {!isMobileView && tournamentHeaderCells}
              <th scope="col" className="lbv2__summary-round">{roundLabel}</th>
              <th scope="col" className="lbv2__summary-total">{t('total', 'Total')}</th>
              {isMobileView && tournamentHeaderCells}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row" className="lbv2__summary-name">{row.name}</th>
                {!isMobileView && tournamentValueCells(row)}
                <td className="lbv2__summary-round">{formatValue(row.roundValue)}</td>
                <td className="lbv2__summary-total">{formatValue(row.totalValue)}</td>
                {isMobileView && tournamentValueCells(row)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

EventSummaryTable.propTypes = {
  metric: PropTypes.oneOf([ 'points', 'purse' ]).isRequired,
  isTeam: PropTypes.bool,
  expanded: PropTypes.bool,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  tournaments: PropTypes.array,
  rows: PropTypes.array,
  currencySymbol: PropTypes.string,
  purseDecimalPlaces: PropTypes.number,
}

export default EventSummaryTable
